import React, { useMemo, useState } from 'react';
import { Badge } from '../../../../../../../components/ui/badge';
import { Button } from '../../../../../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../../../components/ui/table';
import { cn } from '../../../../../../../lib/utils';
import { filterNcrListForWeldPanel, type NcrRow, type SpoolTrackerWeldStepType } from '../../spoolNcrApi';

function statusBadgeCls(v: NcrRow['statusVariant']) {
  if (v === 'pass') return 'border-transparent bg-emerald-50 text-emerald-800';
  if (v === 'warn') return 'border-transparent bg-amber-50 text-amber-900';
  return 'border-transparent bg-red-50 text-red-800';
}

export interface WorkflowNcrSummaryTableProps {
  /** Full list from Redux / GET ncr (same store as NCR register). */
  ncrList: NcrRow[];
  loading?: boolean;
  step: SpoolTrackerWeldStepType;
  stepTitle: string;
  /** Current weld id when filtering per joint; omitted = all welds in spool. */
  weldId?: string | null;
  onRowClick: (row: NcrRow) => void;
  /** Optional CTA to start a repair weld panel from the selected NCR row. */
  onAddRepairWeld?: (row: NcrRow) => void;
  /** Shown under the section title when `onAddRepairWeld` is set (e.g. from `getRepairStripInsertionSummary`). */
  repairPlanHint?: string;
  /** Row action button label; default “Add Repair Weld”. */
  repairActionLabel?: string;
  className?: string;
}

/**
 * Per–weld-step table: default shows **saved (submitted)** NCRs; optional to include in-progress draft rows.
 * Reuse under any workflow panel (fit-up, VT, etc.).
 */
export function WorkflowNcrSummaryTable({
  ncrList,
  loading = false,
  step,
  stepTitle,
  weldId = null,
  onRowClick,
  onAddRepairWeld,
  repairPlanHint,
  repairActionLabel = 'Add Repair Weld',
  className,
}: WorkflowNcrSummaryTableProps) {
  const [includeDrafts, setIncludeDrafts] = useState(true);

  const rows = useMemo(
    () =>
      filterNcrListForWeldPanel(ncrList, {
        step,
        weldId,
        onlySubmitted: !includeDrafts,
      }),
    [ncrList, step, weldId, includeDrafts]
  );

  return (
    <div
      className={cn(
        'w-full min-w-0 space-y-2 overflow-x-hidden rounded-md border border-gray-200 bg-gray-50/60 p-3 pb-4',
        className
      )}
    >
      <div className="flex min-w-0 flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">NCRs — {stepTitle}</p>
          {onAddRepairWeld && repairPlanHint ? (
            <p className="mt-1 max-w-[42rem] text-[11px] leading-snug text-gray-600">{repairPlanHint}</p>
          ) : null}
        </div>
       
      </div>
      {loading ? (
        <p className="text-xs text-gray-500">Loading NCRs…</p>
      ) :  (
        <Table containerClassName="w-full overflow-x-auto rounded border border-gray-200 bg-white">
          <TableHeader>
            <TableRow className="hover:bg-gray-50">
              <TableHead className="text-[10px] font-semibold">NCR #</TableHead>
              <TableHead className="text-[10px] font-semibold">Date</TableHead>
              <TableHead className="text-[10px] font-semibold">Mode</TableHead>
              <TableHead className="text-[10px] font-semibold">Summary</TableHead>
              <TableHead className="text-[10px] font-semibold">Status</TableHead>
              <TableHead className="text-[10px] font-semibold">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer hover:bg-blue-50/70"
                onClick={() => onRowClick(row)}
              >
                <TableCell className="font-mono text-xs font-semibold">{row.ncrNo}</TableCell>
                <TableCell className="text-xs text-gray-700">{row.date}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-[9px] px-1.5',
                      row.ncrSubmissionMode === 'submit'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                        : 'border-slate-200 bg-slate-100 text-slate-700'
                    )}
                  >
                    {row.ncrSubmissionMode === 'submit' ? 'Submitted' : 'Draft'}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-[200px] truncate text-[11px] text-gray-700" title={row.description}>
                  {row.description || '—'}
                </TableCell>
                <TableCell>
                  <Badge className={`text-[9px] ${statusBadgeCls(row.statusVariant)}`}>{row.status}</Badge>
                </TableCell>
                <TableCell>
                  {onAddRepairWeld ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[9px]"
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddRepairWeld(row);
                      }}
                    >
                      {repairActionLabel}
                    </Button>
                  ) : (
                    <span className="text-[10px] text-gray-400">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
